import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Wrench, RotateCcw, ChevronDown, Columns3 } from 'lucide-react';
import api from '../lib/api';
import { itemKey } from '../lib/service';
import { StatusBadge, ExpiryText, formatDate } from '../components/service/ServiceHistory';

// Huolto (#82 versio 5, Superadmin, .claude/maarittelyt/82-huoltokirjaukset.md kohta 7).
// Asiakkaiden monivalinta ja suojatilalista, jonka sarakkeet käyttäjä valitsee itse:
// aina Suojatila, Sijainti ja Sarjanumero, lisäksi kortin tiedot ja suojatiloihin lisätyt
// tarvikkeet nimen mukaan. Rivi vie Suojatilakortille, jossa huolto kirjataan.
// Data: /api/service/snapshot (sama lähde kuin vaiheen B offline-välimuisti).

const inputCls = 'w-full rounded-lg border border-gray-300 text-sm px-3 py-2 focus:outline-none focus:border-brand-500';
const SELECTION_KEY = 'safedigi_service_selection';
const COLUMNS_KEY = 'safedigi_service_columns';
const DEFAULT_COLUMNS = ['f:tenant', 'f:status', 'f:serviced', 'f:notes'];

function readStored(key, valid, fallback) {
  try {
    const v = JSON.parse(localStorage.getItem(key));
    return valid(v) ? v : fallback;
  } catch { return fallback; }
}
function store(key, v) {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* ei localStoragea */ }
}

function useNarrow() {
  const q = '(max-width: 639px)';
  const [narrow, setNarrow] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setNarrow(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return narrow;
}

// Vetovalikko, joka sulkeutuu ulkopuoliseen napautukseen ja Escape-näppäimeen.
function Dropdown({ label, buttonText, icon, className, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open}
        className={`${inputCls} bg-white flex items-center justify-between gap-2 text-left`}>
        <span className="flex items-center gap-1.5 truncate">{icon}{buttonText}</span>
        <ChevronDown size={15} className="text-gray-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-full min-w-[16rem] bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto py-1">
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

// Asiakasvalinta: valintaruudut, "Kaikki asiakkaat" ylimpänä. Nimen napautus valitsee
// vain sen asiakkaan (yleisin käyttö: yksi asiakas kerrallaan).
function TenantPicker({ tenants, selection, onChange }) {
  const { t } = useTranslation();
  const all = selection === 'all';
  const chosen = all ? tenants.map(x => x.id) : selection;
  const label = all ? t('service.allCustomers')
    : chosen.length === 1 ? (tenants.find(x => x.id === chosen[0])?.name || t('service.selectCustomer'))
      : chosen.length > 1 ? t('service.customersSelected', { count: chosen.length })
        : t('service.selectCustomer');

  function toggle(id) {
    const next = chosen.includes(id) ? chosen.filter(x => x !== id) : [...chosen, id];
    onChange(next.length === tenants.length ? 'all' : next);
  }

  return (
    <Dropdown label={t('service.customer')} buttonText={label} className="w-full sm:w-72">
      {close => (
        <>
          <label className="flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-gray-50 cursor-pointer border-b border-gray-100">
            <input type="checkbox" checked={all} onChange={() => onChange(all ? [] : 'all')} />
            {t('service.allCustomers')}
          </label>
          {tenants.map(x => (
            <div key={x.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50">
              <input type="checkbox" checked={chosen.includes(x.id)} onChange={() => toggle(x.id)} aria-label={x.name} />
              <button type="button" onClick={() => { onChange([x.id]); close(); }}
                className="flex-1 text-left text-sm text-gray-800 py-0.5" title={t('service.onlyThisCustomer')}>
                {x.name}
                {x.not_count > 0 && <span className="text-red-700 font-semibold"> · {t('service.notCount', { count: x.not_count })}</span>}
              </button>
            </div>
          ))}
        </>
      )}
    </Dropdown>
  );
}

// Sarakkeiden valinta ryhmissä Suojatilan tiedot ja Tarvikkeet (päätös 27).
function ColumnPicker({ fieldColumns, itemColumns, selected, onChange }) {
  const { t } = useTranslation();
  const toggle = id => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  const row = c => (
    <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 cursor-pointer">
      <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} />
      {c.label}
    </label>
  );
  const heading = text => <p className="px-3 pt-2 pb-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">{text}</p>;
  const count = [...fieldColumns, ...itemColumns].filter(c => selected.includes(c.id)).length;
  return (
    <Dropdown label={t('service.columns')} buttonText={t('service.columnsSelected', { count })}
      icon={<Columns3 size={14} className="text-gray-500" />} className="w-full sm:w-56">
      {() => (
        <>
          {heading(t('service.columnsShelter'))}
          {fieldColumns.map(row)}
          <div className="border-t border-gray-100 mt-1" />
          {heading(t('service.columnsItems'))}
          {itemColumns.length ? itemColumns.map(row)
            : <p className="px-3 py-1.5 text-sm text-gray-400">{t('service.noItemColumns')}</p>}
        </>
      )}
    </Dropdown>
  );
}

// Tarvikesolu: suojatilan samannimiset tarvikkeet yhdessä (määrät yhteen, aikaisin vanheneminen).
function ItemCell({ items }) {
  const { t } = useTranslation();
  if (!items?.length) return <span className="text-gray-300">—</span>;
  const quantities = items.map(i => i.quantity).filter(q => q != null);
  const quantity = quantities.length ? quantities.reduce((a, q) => a + Number(q), 0) : null;
  const models = [...new Set(items.map(i => i.model).filter(Boolean))].join(', ');
  const expires = items.map(i => i.expires_on).filter(Boolean).sort()[0];
  const replace = items.some(i => i.marked_for_replacement);
  return (
    <span className="whitespace-nowrap">
      {quantity != null
        ? <span className="font-medium text-gray-800">{quantity}</span>
        : !models && !expires && <span className="text-green-700">✓</span>}
      {models && <span className="text-gray-500"> {models}</span>}
      {expires && <span className="block text-xs"><ExpiryText value={expires} /></span>}
      {replace && <span className="block text-xs text-amber-700 font-medium">{t('service.markedForReplacement')}</span>}
    </span>
  );
}

const always = [
  { id: 'name', labelKey: 'service.shelter' },
  { id: 'location', labelKey: 'shelter.location' },
  { id: 'serial', labelKey: 'service.serialNumber' },
];

// Puhelimella kortit: nimi, sijainti, sarjanumero ja valitut sarakkeet nimi–arvo-pareina.
function ShelterCards({ shelters, columns }) {
  const { t } = useTranslation();
  return (
    <ul className="space-y-2">
      {shelters.map(s => (
        <li key={s.id}>
          <Link to={`/suojatila/${s.id}`} state={{ from: 'huolto' }}
            className={`block bg-white rounded-xl border px-4 py-3 ${s.last_service?.status === 'not' ? 'border-red-300' : 'border-gray-200'}`}>
            <span className="block font-medium text-gray-900 truncate">{s.name}</span>
            <span className="block text-xs text-gray-500 truncate">
              {[s.location, s.serial_number && `${t('service.serialNumber')} ${s.serial_number}`].filter(Boolean).join(' · ') || '—'}
            </span>
            {columns.length > 0 && (
              <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
                {columns.map(c => (
                  <div key={c.id} className="contents">
                    <dt className="text-gray-500">{c.label}</dt>
                    <dd className="text-gray-800 min-w-0">{c.render(s)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function ShelterTable({ shelters, columns, onOpen }) {
  const { t } = useTranslation();
  const th = 'text-left font-medium px-3 py-2 whitespace-nowrap';
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-xs text-gray-500">
          <tr>
            <th className={`sticky left-0 bg-gray-50 ${th} min-w-[12rem]`}>{t(always[0].labelKey)}</th>
            <th className={th}>{t(always[1].labelKey)}</th>
            <th className={th}>{t(always[2].labelKey)}</th>
            {columns.map(c => <th key={c.id} className={`${th} ${c.wide ? 'min-w-[12rem]' : ''}`}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {shelters.map(s => (
            <tr key={s.id} onClick={() => onOpen(s.id)}
              className={`cursor-pointer hover:bg-gray-50 ${s.last_service?.status === 'not' ? 'bg-red-50/40' : ''}`}>
              <td className="sticky left-0 bg-white px-3 py-2 align-top">
                <Link to={`/suojatila/${s.id}`} state={{ from: 'huolto' }} onClick={e => e.stopPropagation()}
                  className="font-medium text-brand-800 hover:underline">{s.name}</Link>
              </td>
              <td className="px-3 py-2 align-top text-gray-600">{s.location || <span className="text-gray-300">—</span>}</td>
              <td className="px-3 py-2 align-top text-gray-600 whitespace-nowrap">{s.serial_number || <span className="text-gray-300">—</span>}</td>
              {columns.map(c => <td key={c.id} className="px-3 py-2 align-top">{c.render(s)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const dash = <span className="text-gray-300">—</span>;

export default function ServicePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const narrow = useNarrow();

  const [tenants, setTenants] = useState(null);
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // ?asiakas=<id>[,<id>] (esim. vanha huoltopohjan osoite) ohittaa muistetun valinnan.
  const [selection, setSelectionState] = useState(() => {
    const p = params.get('asiakas');
    return p ? p.split(',').filter(Boolean) : readStored(SELECTION_KEY, v => v === 'all' || Array.isArray(v), 'all');
  });
  const [selectedColumns, setSelectedColumnsState] = useState(() => readStored(COLUMNS_KEY, Array.isArray, DEFAULT_COLUMNS));

  const setSelection = useCallback((v) => {
    setSelectionState(v);
    store(SELECTION_KEY, v);
    if (params.get('asiakas')) setParams({}, { replace: true });
  }, [params, setParams]);

  const setSelectedColumns = useCallback((v) => {
    setSelectedColumnsState(v);
    store(COLUMNS_KEY, v);
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const [tn, sn] = await Promise.all([api.get('/api/service/tenants'), api.get('/api/service/snapshot')]);
      setTenants(tn.data);
      setSnap(sn.data);
    } catch (err) {
      setError(err.response?.data?.error || t('common.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const selectedIds = useMemo(() => {
    if (!tenants) return [];
    if (selection === 'all') return tenants.map(x => x.id);
    const known = new Set(tenants.map(x => x.id));
    return selection.filter(id => known.has(id));
  }, [tenants, selection]);

  const shelters = useMemo(() => {
    if (!snap) return [];
    const ids = new Set(selectedIds);
    return snap.shelters.filter(s => ids.has(s.tenant_id));
  }, [snap, selectedIds]);

  // Valittujen suojatilojen tarvikkeet: suojatila → nimen avain → rivit.
  const itemsByShelter = useMemo(() => {
    const ids = new Set(shelters.map(s => s.id));
    const m = new Map();
    for (const i of snap?.items || []) {
      if (!ids.has(i.shelter_id)) continue;
      if (!m.has(i.shelter_id)) m.set(i.shelter_id, new Map());
      const byKey = m.get(i.shelter_id);
      const k = itemKey(i.name);
      byKey.set(k, [...(byKey.get(k) || []), i]);
    }
    return m;
  }, [snap, shelters]);

  const fieldColumns = useMemo(() => {
    const last = s => s.last_service;
    const text = v => (v == null || v === '' ? dash : v);
    return [
      { id: 'f:tenant', label: t('service.customer'), render: s => <span className="text-gray-600 whitespace-nowrap">{s.tenant_name}</span> },
      { id: 'f:status', label: t('service.statusLabel'), render: s => (last(s) ? <StatusBadge status={last(s).status} /> : dash) },
      { id: 'f:serviced', label: t('service.serviced'), render: s => (last(s) ? <span className="text-gray-600 whitespace-nowrap">{formatDate(last(s).performed_at, i18n.language)}</span> : dash) },
      { id: 'f:annual', label: t('shelterCard.annualService'), render: s => (s.annual_service_date ? <span className="text-gray-600 whitespace-nowrap">{formatDate(s.annual_service_date, i18n.language)}</span> : dash) },
      { id: 'f:servicedBy', label: t('service.servicedBy'), render: s => <span className="text-gray-600">{text(last(s)?.performed_by_email)}</span> },
      { id: 'f:notes', label: t('service.notes'), wide: true, render: s => <p className="text-xs text-gray-600 max-w-[16rem] line-clamp-2 whitespace-pre-line">{last(s)?.notes || ''}</p> },
      { id: 'f:personCount', label: t('shelter.personCount'), render: s => <span className="text-gray-600">{text(s.person_count)}</span> },
      { id: 'f:deliveryYear', label: t('shelter.deliveryYear'), render: s => <span className="text-gray-600">{text(s.delivery_year)}</span> },
      { id: 'f:model', label: t('dashboard.list.shelterModel'), render: s => <span className="text-gray-600">{text(s.shelter_model)}</span> },
      { id: 'f:safeDigi', label: t('service.safeDigi'), render: s => <span className="text-gray-600">{s.safe_digi === false ? t('common.no') : t('common.yes')}</span> },
    ];
  }, [t, i18n.language]);

  // Tarvikesarakkeet valittujen suojatilojen tarvikkeista, nimi kirjoitusasultaan
  // tarvikenimien listan mukaan (sama nimi = yksi sarake, päätös 32).
  const itemColumns = useMemo(() => {
    const labels = new Map((snap?.item_names || []).map(n => [itemKey(n), n]));
    const keys = new Set();
    for (const byKey of itemsByShelter.values()) for (const k of byKey.keys()) keys.add(k);
    return [...keys]
      .map(k => ({ id: `i:${k}`, key: k, label: labels.get(k) || k }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fi'))
      .map(c => ({ ...c, render: s => <ItemCell items={itemsByShelter.get(s.id)?.get(c.key)} /> }));
  }, [snap, itemsByShelter]);

  const columns = useMemo(
    () => [...fieldColumns, ...itemColumns].filter(c => selectedColumns.includes(c.id)),
    [fieldColumns, itemColumns, selectedColumns]);

  const openCard = id => navigate(`/suojatila/${id}`, { state: { from: 'huolto' } });

  return (
    <div className="max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Wrench size={18} /> {t('service.title')}</h1>
        <div className="flex flex-wrap sm:flex-nowrap items-end gap-3 w-full sm:w-auto">
          {tenants && <TenantPicker tenants={tenants} selection={selection} onChange={setSelection} />}
          {snap && (
            <ColumnPicker fieldColumns={fieldColumns} itemColumns={itemColumns}
              selected={selectedColumns} onChange={setSelectedColumns} />
          )}
          <button onClick={() => load()} className="text-gray-400 hover:text-gray-700 pb-2.5" title={t('common.refresh')}><RotateCcw size={15} /></button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 mb-4">{error}</div>}
      {loading && <div className="text-center py-16 text-gray-400 text-sm">{t('common.loading')}</div>}
      {!loading && snap && selectedIds.length === 0 && <p className="text-sm text-gray-500">{t('service.intro')}</p>}

      {!loading && snap && selectedIds.length > 0 && (
        <>
          <p className="text-xs text-gray-500 mb-2">{t('service.shelterCount', { count: shelters.length })}</p>
          {shelters.length === 0 && <p className="text-sm text-gray-400">{t('service.noShelters')}</p>}
          {shelters.length > 0 && (narrow
            ? <ShelterCards shelters={shelters} columns={columns} />
            : <ShelterTable shelters={shelters} columns={columns} onOpen={openCard} />)}
        </>
      )}
    </div>
  );
}
